import axios from 'axios';
import { isEmpty } from 'lodash';
import pathfinder from '@fiverr-private/pathfinder';
import { logger, stats } from '@fiverr-private/obs';
import {
    FETCH_REVIEWS,
    FILTER_BY_TYPES,
    FILTER_TYPES,
    NO_REVIEWS,
    RECENT_SORT,
    SORT_BY_TYPES,
    STATS_PREFIX,
} from '../../constants';
import { isHttpOk } from '../../utils/httpResponse';
import dynamicTranslationService from '../../utils/translation/DynamicTranslationService';
import { getTotalHighlightsAmount } from '../../utils/highlighting';
import { USER, GIG, STUDIO, PAGE_SIZE, PATHFINDER_RATINGS_INDEX } from '../constants';
import { updateReviews } from '../actions';

const getIdAndType = ({ gigId, userId, studioId }) => {
    const res = {};

    if (gigId) {
        res.id = gigId;
        res.type = GIG;
    } else if (userId) {
        res.id = userId;
        res.type = USER;
    } else if (studioId) {
        res.id = studioId;
        res.type = STUDIO;
    }
    return res;
};

const buildFetchReviewsUrl = (pathFinderConfig) => {
    if (pathFinderConfig) {
        const { path, params } = pathFinderConfig;
        return pathfinder(path, params);
    }

    return pathfinder(PATHFINDER_RATINGS_INDEX, null);
};

const buildFiltersParams = (filterType, filters = {}) => {
    const params = {};

    const filtersKeysArray = Object.entries(filters)
        .filter(([, value]) => value)
        .map(([key]) => key);

    if (SORT_BY_TYPES.includes(filterType)) {
        params.sort_by = filterType === FILTER_TYPES.recent ? RECENT_SORT : filterType;
    } else if (FILTER_BY_TYPES.includes(filterType)) {
        filtersKeysArray.push(filterType);
    }

    if (!isEmpty(filtersKeysArray)) {
        params.filter_by = filtersKeysArray.join(',');
    }

    return params;
};

const buildFetch = ({
    idType,
    id,
    asSeller = false,
    lastReviewDetails,
    filterType,
    selectedStars,
    filters = {},
    currentIndustries,
    selectedCommonIndustry,
    ignoredReviewIds,
    roleId,
}) => {
    const params = {};

    params[`${idType}_id`] = id;

    if (idType === USER) {
        params.as_seller = asSeller.toString();
    }

    if (roleId) {
        params.seller_role_id = roleId;
    }

    if (lastReviewDetails) {
        Object.assign(params, { ...prepareFilters(lastReviewDetails) });

        if (!isEmpty(ignoredReviewIds)) {
            Object.assign(params, { ignored_review_ids: ignoredReviewIds.join() });
        }
    }

    if (selectedStars && !isEmpty(selectedStars) && filters.breakdown) {
        params.average_valuation_value = selectedStars[0];
    }

    if (!isEmpty(currentIndustries)) {
        Object.assign(params, { current_user_industry: Object.keys(currentIndustries).join() });
    }

    if (selectedCommonIndustry) {
        Object.assign(params, { industry_filter_value: selectedCommonIndustry });
    }

    if (filters[FILTER_TYPES.text]) {
        Object.assign(params, { text_filter_value: filters[FILTER_TYPES.text] });
    }

    Object.assign(params, buildFiltersParams(filterType, filters));

    params.page_size = PAGE_SIZE;

    return { params };
};

const prepareFilters = (lastReviewDetails) => {
    const { id, score } = lastReviewDetails;
    const params = {};
    if (id) {
        params.last_star_rating_id = id;
        params.last_review_id = id; // freelancer BE recognize only "last_review_id" param
    }
    if (score || score === 0) {
        params.last_score = score;
    }

    return params;
};

const fetchReviews =
    ({ filterType, isFilter, lastReviewDetails }) =>
    async (dispatch, getState) => {
        const state = getState();
        const {
            user_id: userId,
            gig_id: gigId,
            studio_id: studioId,
            biEventGroup: origin,
            selectedStars,
            pathFinderConfig,
            reviews_as_seller: asSeller,
            industryDropdownSelected: selectedCommonIndustry,
            filters,
            pageName,
            pageCtxId,
            role_id: roleId,
            filters_counters: filterCounters,
            ignored_review_ids: ignoredReviewIds,
        } = state;

        const { id, type } = getIdAndType({ gigId, studioId, userId });
        const currentIndustries = filterCounters?.current_user_industries;

        const params = buildFetch({
            id,
            idType: type,
            asSeller,
            selectedStars,
            lastReviewDetails,
            filterType,
            filters,
            currentIndustries,
            selectedCommonIndustry,
            ignoredReviewIds,
            roleId,
        });

        const path = buildFetchReviewsUrl(pathFinderConfig);

        try {
            const { status, statusText, data } = (await axios.get(path, params)) || {};
            let itemsCount = 0,
                highlightedAmount = null,
                textFilterResultsAmount = null;

            if (!isHttpOk(status)) {
                throw new Error(statusText);
            }

            if (isEmpty(data)) {
                stats.count(STATS_PREFIX, NO_REVIEWS);
                return {};
            }

            if (isEmpty(data.reviews)) {
                stats.count(STATS_PREFIX, NO_REVIEWS);
            } else {
                // README: Covered by enriching a translations within SDK
                dynamicTranslationService.addIndustriesTranslations(data.dynamicTranslations);

                itemsCount = data.reviews.length;
                highlightedAmount = getTotalHighlightsAmount(data.reviews);
                textFilterResultsAmount = data?.filters_counters?.text || data?.total_count;
            }

            dispatch(updateReviews(data, filterType, isFilter));

            return {
                sellerId: userId,
                gigId,
                itemsCount,
                origin,
                studioId,
                pageName,
                pageCtxId,
                filterType,
                highlightedAmount,
                textFilterResultsAmount,
            };
        } catch (error) {
            stats.count(STATS_PREFIX, FETCH_REVIEWS);
            logger.error(error, { flow: 'fetch-reviews' });
            throw error;
        }
    };

export default fetchReviews;
